if (!customElements.get("algolia-filters-form")) {
  class AlgoliaFiltersForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector("form");
      this.collectionInput = this.querySelector("input.input-collection");
      this.filterSelects = this.querySelectorAll("select.dropdown-filter");
      this.applyButton = this.querySelector('button[type="submit"]');
      this.applyLabel = this.applyButton?.querySelector(".apply-label");
      this.applyBaseText = this.applyLabel?.textContent || "";
      this.targetUrl = this.dataset.targetUrl || "/collections/all";

      this.setupEventListeners();
      this.load();
    }

    setupEventListeners() {
      this.form.addEventListener("submit", this.onApply.bind(this));
      this.filterSelects.forEach((select) => {
        select.addEventListener("change", this.onFilterChange.bind(this));
      });
    }

    getCollectionId() {
      return this.collectionInput?.dataset.id || "";
    }

    shouldUseShopify() {
      return typeof window.LWSAlgolia?.shouldUseShopify === "function"
        ? window.LWSAlgolia.shouldUseShopify()
        : false;
    }

    escape(value) {
      return String(value).replaceAll("\\", "\\\\").replaceAll('"', '\\"');
    }

    buildCollectionFilter() {
      const collectionId = this.getCollectionId();

      return collectionId ? `collection_ids:"${this.escape(collectionId)}"` : "";
    }

    getSelectedFilters() {
      const selected = [];

      this.filterSelects.forEach((select) => {
        const attribute = select.dataset.attribute;
        const value = select.value;

        if (attribute && value) {
          selected.push({ attribute, value });
        }
      });

      return selected;
    }

    buildFilters() {
      const parts = [];
      const collectionFilter = this.buildCollectionFilter();

      if (collectionFilter) {
        parts.push(collectionFilter);
      }

      this.getSelectedFilters().forEach(({ attribute, value }) => {
        parts.push(`${attribute}:"${this.escape(value)}"`);
      });

      return parts.join(" AND ");
    }

    async queryFacets(includeSelected = false) {
      if (this.shouldUseShopify()) {
        return this.queryShopifyFacets(includeSelected);
      }

      if (!window.LWSAlgolia) return null;

      if (!window.LWSAlgolia.state?.client) {
        window.LWSAlgolia.init();
      }

      const client = window.LWSAlgolia.state?.client;

      if (!client) return null;

      const filters = includeSelected ? this.buildFilters() : this.buildCollectionFilter();

      try {
        const response = await client.search([
          {
            indexName: window.LWSAlgolia.config.indexName,
            params: {
              query: "",
              hitsPerPage: 1,
              filters,
              facets: ["*"],
              maxValuesPerFacet: 100
            }
          }
        ]);

        return response.results[0];
      } catch (error) {
        console.error("Algolia filters query error:", error);
        return null;
      }
    }

    async queryShopifyFacets(includeSelected = false) {
      const url = new URL(window.location.pathname, window.location.origin);
      url.searchParams.set("view", "products-json");

      if (includeSelected) {
        this.getSelectedFilters().forEach(({ attribute, value }) => {
          const shopifyParam = window.LWSAlgolia.api.algoliaToShopifyFilterParam(attribute);
          if (shopifyParam) {
            url.searchParams.append(shopifyParam, value);
          }
        });
      }

      try {
        const response = await fetch(url.toString());
        if (!response.ok) return null;

        const data = await response.json();

        const facets = {};
        const excludedParams = [
          "filter.v.availability",
          "filter.p.product_type"
        ];

        (data.filters || []).forEach((filter) => {
          const paramName = filter.param_name || "";
          if (excludedParams.includes(paramName)) return;
          if (filter.type === "price_range") return;
          if (!filter.values || !filter.values.length) return;

          const facetName = window.LWSAlgolia.api.shopifyParamToFacet(paramName);
          facets[facetName] = {};
          filter.values.forEach((v) => {
            facets[facetName][v.value] = v.count;
          });
        });

        return { facets, nbHits: data.count || 0 };
      } catch (error) {
        console.error("Shopify filters query error:", error);
        return null;
      }
    }

    clearOptions(select) {
      const options = select.querySelectorAll("option:not(.default)");

      options.forEach((option) => {
        select.removeChild(option);
      });

      select.querySelector("option.default")?.setAttribute("selected", "selected");
      select.selectedIndex = 0;
    }

    setLoading(isLoading) {
      this.classList.toggle("is-loading", isLoading);
      this.applyButton?.classList.toggle("loading", isLoading);
    }

    updateApplyCount(count) {
      if (!this.applyLabel || typeof count !== "number") return;

      this.applyLabel.textContent = `${this.applyBaseText} (${count})`;
    }

    formatOptionLabel(value) {
      if (String(value) === "true") return "Oui";
      if (String(value) === "false") return "Non";

      return String(value).replace(/\.0$/, "");
    }

    populateSelects(response) {
      this.filterSelects.forEach((select) => {
        const attribute = select.dataset.attribute;
        const values = response.facets[attribute] || {};

        this.clearOptions(select);

        Object.entries(values)
          .filter(([, count]) => count > 0)
          .sort((a, b) => b[1] - a[1])
          .forEach(([value]) => {
            const option = document.createElement("option");
            option.value = value;
            option.textContent = this.formatOptionLabel(value);
            select.appendChild(option);
          });
      });
    }

    async load() {
      this.setLoading(true);

      const response = await this.queryFacets(false);

      this.setLoading(false);

      if (!response?.facets) return;

      this.populateSelects(response);
      this.updateApplyCount(response.nbHits);
    }

    async refreshCount() {
      this.setLoading(true);

      const response = await this.queryFacets(true);

      this.setLoading(false);

      if (response) {
        this.updateApplyCount(response.nbHits);
      }
    }

    onFilterChange() {
      this.refreshCount();
    }

    onApply(event) {
      event.preventDefault();

      if (this.shouldUseShopify()) {
        const collectionUrl = this.collectionInput?.value || this.targetUrl;
        const url = new URL(collectionUrl, window.location.origin);
        url.searchParams.set("view", "products");

        this.getSelectedFilters().forEach(({ attribute, value }) => {
          url.searchParams.append(`filter.a.${attribute}`, value);
        });

        window.location.href = url.toString();
        return;
      }

      const languageRoot = window.LWSAlgoliaSettings?.languageRootUrl || "";
      let target = this.targetUrl;

      if (languageRoot && languageRoot !== "/" && target.startsWith("/")) {
        target = languageRoot + target;
      }

      const url = new URL(target, window.location.origin);

      const collectionId = this.getCollectionId();
      if (collectionId) {
        url.searchParams.append("filter.a.collection_ids", collectionId);
      }

      this.getSelectedFilters().forEach(({ attribute, value }) => {
        url.searchParams.append(`filter.a.${attribute}`, value);
      });

      window.location.href = url.toString();
    }
  }

  customElements.define("algolia-filters-form", AlgoliaFiltersForm);
}
